import dayjs from 'dayjs';

import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { formInputKeys } from '../../constants';
import { SelectInputProps, TerminationSimulationInputs } from '../../types';

const InputLabel = ({ label }: { label: string }) => {
  return (
    <Typography fontSize={12} variant="subtitle1" mb={1} fontWeight="500">
      {label} *
    </Typography>
  );
};

const enabledInputs = {
  comJustaCausa: [formInputKeys.paymentDate],
  semJustaCausa: [
    formInputKeys.noticeType,
    formInputKeys.noticeStartDate,
    formInputKeys.fgtsBalance,
    formInputKeys.paymentDate,
  ],
  rescisaoAntecipadaDoContratoDeExperienciaPeloEmpregador: [
    formInputKeys.noticeType,
    formInputKeys.paymentDate,
  ],
  rescisaoAntecipadaDoContratoDeExperienciaPeloEmpregado: [
    formInputKeys.noticeType,
    formInputKeys.paymentDate,
  ],
  rescisaoJovemAprendizATermo: [
    formInputKeys.noticeType,
    formInputKeys.paymentDate,
  ],
  pedidoDeDemissao: [
    formInputKeys.noticeType,
    formInputKeys.noticeStartDate,
    formInputKeys.paymentDate,
  ],
  falecimento: [formInputKeys.fgtsBalance, formInputKeys.paymentDate],
  extincaoDeContratoDeExperiencia: [
    formInputKeys.noticeType,
    formInputKeys.noticeStartDate,
    formInputKeys.paymentDate,
  ],
  mutuoAcordo: [
    formInputKeys.noticeType,
    formInputKeys.noticeStartDate,
    formInputKeys.fgtsBalance,
    formInputKeys.paymentDate,
  ],
};

export const shouldShowInput = (
  reason: TerminationSimulationInputs['reason'],
  inputKey: keyof TerminationSimulationInputs,
) => {
  const enabledInputsForReason = enabledInputs?.[reason];
  return enabledInputsForReason?.includes(inputKey);
};

export const SelectInput = ({
  inputKey,
  getInputProps,
  label,
  placeholder,
  options,
  error = false,
  helperText = '',
}: SelectInputProps) => {
  const { value, onChange } = getInputProps(inputKey);
  const formatedOptions = Object.entries(options).map(([key, value]) => ({
    label: value,
    value: key,
  }));
  return (
    <Box>
      <InputLabel label={label} />
      <Select
        value={value || ''}
        onChange={onChange}
        fullWidth
        required
        error={error}
        displayEmpty
        renderValue={(selected: string | null) => {
          if (!selected) return placeholder;
          const option = formatedOptions?.find(
            (option) => option.value === selected,
          );
          return option?.label || selected;
        }}
      >
        {formatedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {String(option.label)}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography
          color={error ? 'error' : 'inherit'}
          variant="caption"
          marginTop={1}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export const DatePickerInput = <T extends string>({
  inputKey,
  getInputProps,
  label,
  error = false,
  helperText = '',
}: {
  inputKey: T;
  getInputProps: (key: T) => { value: string; onChange: (e: any) => void };
  label: string;
  error?: boolean;
  helperText?: string;
}) => {
  const { value, onChange } = getInputProps(inputKey);

  const handleChange = (newValue: any) => {
    onChange(newValue ? newValue.format('YYYY-MM-DD') : '');
  };

  return (
    <Box>
      <InputLabel label={label} />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          label=""
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error,
              helperText,
              placeholder: 'dd/mm/aaaa',
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export const TextFieldInput = <T extends string>({
  inputKey,
  getInputProps,
  label,
  placeholder,
  error = false,
  helperText = '',
}: {
  inputKey: T;
  getInputProps: (key: T) => { value: string; onChange: (e: any) => void };
  label: string;
  placeholder: string;
  error?: boolean;
  helperText?: string;
}) => {
  const { value, onChange } = getInputProps(inputKey);

  return (
    <Box>
      <InputLabel label={label} />
      <TextField
        value={value}
        onChange={onChange}
        fullWidth
        required
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
};

export const NoticeDaysInput = <T extends string>({
  getInputProps,
  label,
  placeholder,
}: {
  getInputProps: (key: T) => { value: string; onChange: (e: any) => void };
  label: string;
  placeholder: string;
}) => {
  const { value: terminationDate } = getInputProps('terminationDate' as T);
  const { value: noticeStartDate } = getInputProps('noticeStartDate' as T);
  const days = dayjs(terminationDate).diff(dayjs(noticeStartDate), 'days');
  return (
    <Box>
      <InputLabel label={label} />
      <TextField
        value={days}
        type="number"
        disabled
        fullWidth
        placeholder={placeholder}
      />
    </Box>
  );
};
